/* eslint no-param-reassign: "error" */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getInitialDataV2 } from '../../services/authService';
import { requestStatusType } from '../../types';
import { setFolders } from '../library/librarySlice';
import { setUser } from '../settings/settingsSlice';
import { setSubscriptionState } from '../plan/planSlice';
import { fetchSubscription } from '../plan/thunk';
import { getTeamPictureUrlThunk, setTeamState } from '../team/teamSlice';
import { setGlobalSettings } from '../globalSettings/globalSettingsSlice';
import { removeToken, shouldRefreshToken } from '../../lib/localStorageHandler';
import { refreshToken } from '../auth/authThunks';
import logger from '../../lib/logger';
import { getPluginData } from '../plugin/pluginSlice';
import { sendEmail } from '../../services/emailService';
import StringExtension from '../../utils/secure';
import { setSentryPlan, setSentryTeam, setSentryUser } from '../../lib/sentry';

export interface InitialRequestOptions {
  joinCode?: string;
  zoomCode?: string;
  state?: string;
}

interface InitialStateErrorPayload {
  authFailed: boolean;
  message: string;
}

type InitialStateSuccessPayload = Pick<
  Awaited<ReturnType<typeof getInitialDataV2>>,
  'globalSettings' | 'settings'
>;

const getErrorStatus = (error: unknown): number | null => {
  if (
    typeof error !== 'object' ||
    error === null ||
    !('response' in error) ||
    typeof (error as { response?: { status?: unknown } }).response?.status !==
      'number'
  ) {
    return null;
  }

  return (error as { response: { status: number } }).response.status;
};

const isAuthenticationError = (error: unknown): boolean => {
  const status = getErrorStatus(error);
  return status === 401 || status === 403;
};

const getInitialStateErrorMessage = (error: unknown): string => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { data?: { message?: unknown } } }).response
      ?.data?.message === 'string'
  ) {
    return (error as { response: { data: { message: string } } }).response.data
      .message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Failed to load your account data.';
};

export const getInitialState = createAsyncThunk<
  InitialStateSuccessPayload,
  InitialRequestOptions | void,
  { rejectValue: InitialStateErrorPayload }
>(
  'app/getInitialState',
  async (
    options: InitialRequestOptions = {},
    { dispatch, rejectWithValue },
  ) => {
    try {
      // Check if token needs refresh before loading initial data
      if (shouldRefreshToken()) {
        await dispatch(refreshToken());
      }

      const { joinCode, zoomCode, state } = options;

      // Only pass `joinCode` or `zoomCode` to the API if provided
      const apiOptions: InitialRequestOptions = {};
      if (joinCode) {
        apiOptions.joinCode = new StringExtension(joinCode).sanitize();
      }
      if (zoomCode) {
        apiOptions.zoomCode = new StringExtension(zoomCode).sanitize();
      }
      if (state) {
        apiOptions.state = new StringExtension(state).sanitize();
      }

      // Fetch initial data based on the options
      const {
        folders,
        user,
        team,
        packageProperties,
        usage,
        settings,
        integrations,
        globalSettings,
      } = (await getInitialDataV2(apiOptions)) || {};

      const {
        _id,
        email,
        profile,
        role,
        deleteRequestedAt,
        marketing = {},
        createdAt,
        tags = {},
        customButtons = [],
      } = user || {};
      const { name, picture, locale } = profile || {};
      const { teams, selectedTeam } = team;

      // Dispatch data to respective slices
      await dispatch(setFolders(folders));
      await dispatch(
        setUser({
          _id,
          email,
          name,
          picture,
          role,
          deleteRequestedAt,
          createdAt,
          tags,
          integrations,
          locale,
          customButtons,
        }),
      );
      await dispatch(
        setTeamState({
          status: 'succeeded',
          teams,
          selectedTeam,
        }),
      );
      await dispatch(
        setSubscriptionState({
          usage,
          packageProperties,
          plan: selectedTeam.subscription,
          selectedPlan: ['BUSINESS', 'GROWTH'].includes(
            selectedTeam.subscription.package,
          )
            ? (selectedTeam.subscription.package as 'BUSINESS' | 'GROWTH')
            : 'GROWTH',
          marketing,
        }),
      );

      // Fetch full subscription details (includes payer info)
      dispatch(fetchSubscription({ teamId: selectedTeam.teamId }));

      await dispatch(getPluginData());

      // Dispatch global settings to Redux state
      if (globalSettings) {
        await dispatch(
          setGlobalSettings({
            aiChatEnabled: globalSettings.aiChatEnabled,
          }),
        );
      }

      // Initialize Tolt if applicable
      if (window.tolt_referral) {
        window.tolt?.signup(user.email);
      }

      // Set Sentry user and team context for error tracking
      if (_id) {
        setSentryUser({ id: _id, email });
      }
      if (selectedTeam?.teamId) {
        setSentryTeam({ teamId: selectedTeam.teamId });
      }
      if (selectedTeam?.subscription?.package) {
        setSentryPlan({ package: selectedTeam.subscription.package });
      }

      if (team.selectedTeam?.teamId) {
        dispatch(getTeamPictureUrlThunk(team.selectedTeam?.teamId));
      }

      // Return settings and global settings for further usage
      return { settings, globalSettings };
    } catch (error) {
      logger.error('initial data error', error);
      const authFailed = isAuthenticationError(error);

      if (authFailed) {
        removeToken();
      }

      return rejectWithValue({
        authFailed,
        message: getInitialStateErrorMessage(error),
      } satisfies InitialStateErrorPayload);
    }
  },
);

export const sendEmailThunk = createAsyncThunk(
  'app/sendEmail',
  async (emailData: {
    template: string;
    toEmail: string;
    data: {
      content: string;
      [key: string]: any;
    };
  }) => {
    try {
      const response = await sendEmail(emailData);
      return response.data;
    } catch (error) {
      logger.error('send email error', error);
      throw error;
    }
  },
);

export interface AppState {
  authFailed: boolean;
  errorMessage: string | null;
  status: requestStatusType;
}

const initialState: AppState = {
  authFailed: false,
  errorMessage: null,
  status: 'idle',
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getInitialState.pending, (state) => {
        state.authFailed = false;
        state.errorMessage = null;
        state.status = 'pending';
      })
      .addCase(getInitialState.fulfilled, (state) => {
        state.authFailed = false;
        state.errorMessage = null;
        state.status = 'succeeded';
      })
      .addCase(getInitialState.rejected, (state, action) => {
        state.authFailed = Boolean(action.payload?.authFailed);
        state.errorMessage =
          action.payload?.message ||
          action.error.message ||
          'Failed to load your account data.';
        state.status = 'failed';
      })
      .addCase(sendEmailThunk.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(sendEmailThunk.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(sendEmailThunk.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export default appSlice.reducer;
